import React, {useState, useCallback, useMemo} from 'react';
import {useFormik, FormikProvider, Form} from 'formik';
import {useSelector} from 'react-redux';
import {useSnackbar} from 'notistack';
import {startCase} from 'lodash';
// mui
import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  TextField,
  MenuItem,
  FormLabel,
  Autocomplete
} from '@mui/material';
// redux
import {useDispatch} from 'src/redux/store';
import {createFile} from 'src/redux/slices/file';
import {addClientFile} from 'src/redux/slices/client';
// component
import {UploadSingleFile} from 'src/components/upload';
// constants
import {DOC_SUBTYPE, DOC_TYPES} from 'src/constants/file-vault';
import useAuth from 'src/hooks/useAuth';
// ----------------------------------------------------------------------

const AddNewDialog = ({open, handleClose,onAddFile, title,other={
  documentType: '',
  subType: '',
  fileType: '',
  itemId: '',
  documentTitle: '',
  post: null} }) => {
  const {user} = useAuth();
  const isAdvisor = user.role === 'Advisor';

  // NOTE: advisor stuff
  const {list} = useSelector((state) => state.client);
  const {members} = useSelector((state) => state.familyGroup);

  const clientId = (isAdvisor ? list : members).find((item) => item.show)?.id || '';
  const memberSelected = members.filter((m) => m.show && m.id !== user.userId);

  // NOTE: advisor stuff

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const {enqueueSnackbar} = useSnackbar();

  const {isLoading: aLoading, assets} = useSelector((state) => state.asset);
  const {isLoading: iLoading, insurance} = useSelector((state) => state.insurance);
  const {isLoading: oLoading, org} = useSelector((state) => state.organization);

  const {asset: advAssets, insurance: advIns, org: advOrg} = useSelector((state) => state.client);

  const itemValues = useMemo(
    () => ({
      asset: isAdvisor || memberSelected.length === 1 ? advAssets : assets,
      policy: isAdvisor || memberSelected.length === 1 ? advIns : insurance,
      entity: isAdvisor || memberSelected.length === 1 ? advOrg : org,
      claim: DOC_SUBTYPE.claim.reduce(
        (res, {value}) => ({
          ...res,
          [value]: (isAdvisor ? advIns : insurance).claim.filter((claim) => claim.claimType === value)
        }),
        {}
      )
    }),
    [assets, insurance, org, isAdvisor, advAssets, advIns, advOrg, memberSelected]
  );

  const formik = useFormik({
    initialValues: other,
    onSubmit: async (values) => {
      setLoading(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });
      try {
        const res =
          !!clientId || memberSelected.length === 1
            ? await dispatch(addClientFile(formData, clientId, !isAdvisor))
            : await dispatch(createFile(formData));

        setLoading(false);
        if (res && res?.data) {
          enqueueSnackbar('Upload file success.', {variant: 'success'});
          onAddFile(res?.data);
        } else {
          enqueueSnackbar(res?.message || 'Upload file error.', {variant: 'error'});
        }
        formik.resetForm();
        handleClose();
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(err.message || 'Upload file failed.', {variant: 'error'});
      }
    }
  });

  const {handleSubmit, getFieldProps, setFieldValue, values, errors, touched} = formik;
  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setFieldValue('post', file);
      }
    },
    [setFieldValue]
  );

  const targetSubTypeIndex = DOC_SUBTYPE[values.documentType]?.findIndex((item) => item.value === values.subType);
  const fileTypes = targetSubTypeIndex > -1 ? DOC_SUBTYPE[values.documentType][targetSubTypeIndex].fileTypes || [] : [];

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Dialog open={open} maxWidth="xs" fullWidth onClose={handleClose}>
          <DialogTitle>{title}</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2}>
              <TextField fullWidth select label="File Type" {...getFieldProps('documentType')}>
                {DOC_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
              {!!values.documentType && (
                <TextField
                  select
                  fullWidth
                  label="Subtype"
                  {...getFieldProps('subType')}
                  onChange={(e) => setFieldValue('subType', e.target.value)}
                >
                  {DOC_SUBTYPE[values.documentType]?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {!!values.subType && (
                <TextField
                  select
                  fullWidth
                  label="File Type"
                  {...getFieldProps('fileType')}
                  onChange={(e) => setFieldValue('fileType', e.target.value)}
                >
                  {fileTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {!!values.documentType &&
                !!values.subType &&
                values.documentType !== 'government' &&
                !aLoading &&
                !iLoading &&
                !oLoading && (
                  <Autocomplete
                    fullWidth
                    options={itemValues[values.documentType]?.[values.subType]?.filter(
                      (option) => !clientId || option.user === clientId
                    )}
                    getOptionLabel={(option) => option.nickName}
                    isOptionEqualToValue={(option, value) => option.id === value}
                    freeSolo
                    onChange={(e, option) => setFieldValue('itemId', option.id)}
                    disableClearable
                    value={
                      itemValues[values.documentType]?.[values.subType]?.find(
                        (option) => option.id === values.itemId
                      ) || null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoComplete="off"
                        label={startCase(values.documentType)}
                        variant="outlined"
                        value={values.itemId}
                        onChange={(e) => setFieldValue('itemId', e.target.value)}
                      />
                    )}
                  />
                )}
              <TextField fullWidth label="File name(override, optional)" {...getFieldProps('documentTitle')} />
              <Stack>
                <FormLabel>File</FormLabel>
                <UploadSingleFile file={values.post} onDrop={handleDrop} error={Boolean(touched.post && errors.post)} />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <LoadingButton type="submit" variant="contained" loading={loading} onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Form>
    </FormikProvider>
  );
};

export default AddNewDialog;
